import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from "../networking/axiosconfig";
import InformationalPopup from '../component/InformationalPopup';
import { Toaster } from 'react-hot-toast';
import { Calendar, ArrowLeft, Save } from 'lucide-react';
import Navbar from './Navbar';

export default function LeaveAddScreen() {
    const navigate = useNavigate();
    const [startDate, setStartDate] = useState('');
    const [isPopupVisible, setPopupVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [popupMessage, setPopupMessage] = useState("");
    const [popupTitle, setPopupTitle] = useState("");

    const [LeaveFormData, setLeaveFormData] = useState({
        user_id: "",
        leave_type_id_FK: '',
        start_date: '',
        end_date: '',
        reason: ''
    });

    const handleStartDateChange = (e) => {
        const selectedDate = e.target.value;
        setStartDate(selectedDate);

        if (selectedDate) {
            const formattedDate = new Date(selectedDate).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            });

            setLeaveFormData((prev) => ({
                ...prev,
                start_date: selectedDate,
                formatted_start_date: formattedDate,
                end_date: prev.end_date && prev.end_date < selectedDate ? '' : prev.end_date
            }));
        }
    };

    const handleendDateChange = (e) => {
        const selectedDate = e.target.value;

        if (selectedDate) {
            const formattedDate1 = new Date(selectedDate).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            });

            setLeaveFormData((prev) => ({
                ...prev,
                end_date: selectedDate,
                formatted_end_date: formattedDate1,
            }));
        }
    };

    useEffect(() => {
        if (isPopupVisible) {
            const timer = setTimeout(() => {
                setPopupVisible(false);
            }, 2000);

            return () => clearTimeout(timer);
        }
    }, [isPopupVisible]);

    useEffect(() => {
        const storedUserId = localStorage.getItem("userId") || "";
        setLeaveFormData((prev) => ({ ...prev, user_id: storedUserId }));
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setLeaveFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    const handleFormSubmit = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.post(`/api/leave`, {
                user_id: LeaveFormData.user_id.trim(),
                leave_type_id_FK: LeaveFormData.leave_type_id_FK.trim(),
                start_date: LeaveFormData.start_date,
                end_date: LeaveFormData.end_date,
                reason: LeaveFormData.reason.trim(),
            });

            if (response.data.status) {
                setPopupTitle("Leave Added Successfully");
                setPopupMessage("Your leave request has been submitted.");
                setPopupVisible(true);

                setTimeout(() => {
                    setPopupVisible(false);
                    navigate('/employee/leave');
                }, 2000);
            } else {
                setPopupTitle("Leave Request Failed");
                setPopupMessage(response.data.message);
                setPopupVisible(true);

                setTimeout(() => {
                    setPopupVisible(false);
                }, 5000);
            }

        } catch (error) {
            console.error('Error adding leave:', error);

            setPopupTitle("Error");
            if (error.response) {
                setPopupMessage(error.response.data.message || "Something went wrong.");
            } else {
                setPopupMessage("Network error. Please try again.");
            }
            setPopupVisible(true);

            setTimeout(() => {
                setPopupVisible(false);
            }, 2000);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        axiosInstance.get(`api/leave/leave/type`)
            .then((response) => {
                if (response.data.status && Array.isArray(response.data.data)) {
                    setLeaveTypes(response.data.data);
                } else {
                    console.error("Invalid API response:", response.data);
                }
            })
            .catch((error) => console.error("Error fetching leave types:", error));
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        const validationErrors = validateLeaveForm(LeaveFormData);
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length === 0) {
            handleFormSubmit();
        } else {
            console.error("Validation errors:", validationErrors);
        }
    };

    const validateLeaveForm = (data) => {
        let errors = {};
        if (!data.leave_type_id_FK.trim()) errors.leave_type_id_FK = 'Please select a leave type';
        if (!data.start_date.trim()) errors.start_date = 'Please select a start date';
        return errors;
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
            <Navbar />
            <Toaster position="top-right" reverseOrder={false} />

            <div className="max-w-4xl mx-auto p-6">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 rounded-2xl shadow-2xl p-8 mb-6 text-white">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                                <Calendar size={32} />
                            </div>
                            <div>
                                <h1 className="text-3xl md:text-4xl font-bold mb-2">Apply for Leave</h1>
                                <p className="text-blue-100">Fill in the details below to submit your leave request</p>
                            </div>
                        </div>
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center gap-2 px-6 py-3 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-semibold shadow-lg"
                        >
                            <ArrowLeft size={20} />
                            <span>Back</span>
                        </button>
                    </div>
                </div>

                {/* Form Card */}
                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-xl font-bold text-gray-800">Leave Request Form</h2>
                        <p className="text-sm text-gray-500 mt-1">Fields marked with * are required</p>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6 space-y-6">
                        {/* Leave Type */}
                        <div>
                            <label htmlFor="leave_type_id_FK" className="block text-sm font-semibold text-gray-700 mb-2">
                                Leave Type <span className="text-red-500">*</span>
                            </label>
                            <select
                                id="leave_type_id_FK"
                                name="leave_type_id_FK"
                                value={LeaveFormData.leave_type_id_FK}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                    errors.leave_type_id_FK 
                                        ? "border-red-300 bg-red-50" 
                                        : "border-gray-300"
                                }`}
                            >
                                <option value="">Select leave type...</option>
                                {leaveTypes.map((type) => (
                                    <option key={type.leave_type_id_PK} value={type.leave_type_id_PK}>
                                        {type.name}
                                    </option>
                                ))}
                            </select>
                            {errors.leave_type_id_FK && (
                                <p className="mt-2 text-sm text-red-600">{errors.leave_type_id_FK}</p>
                            )}
                        </div>

                        {/* Date Fields */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {/* Start Date */}
                            <div>
                                <label htmlFor="start_date" className="block text-sm font-semibold text-gray-700 mb-2">
                                    Start Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    id="start_date"
                                    name="start_date"
                                    value={LeaveFormData.start_date}
                                    onChange={handleStartDateChange}
                                    min={new Date().toISOString().split("T")[0]}
                                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                        errors.start_date 
                                            ? "border-red-300 bg-red-50" 
                                            : "border-gray-300"
                                    }`}
                                />
                                {LeaveFormData.formatted_start_date && (
                                    <p className="mt-2 text-sm text-green-600 font-medium">
                                        📅 {LeaveFormData.formatted_start_date}
                                    </p>
                                )}
                                {errors.start_date && (
                                    <p className="mt-2 text-sm text-red-600">{errors.start_date}</p>
                                )}
                            </div>

                            {/* End Date */}
                            <div>
                                <label htmlFor="end_date" className="block text-sm font-semibold text-gray-700 mb-2">
                                    End Date <span className="text-gray-400 font-normal">(optional)</span>
                                </label>
                                <input
                                    type="date"
                                    id="end_date"
                                    name="end_date"
                                    value={LeaveFormData.end_date}
                                    onChange={handleendDateChange}
                                    min={startDate ? new Date(new Date(startDate).setDate(new Date(startDate).getDate() + 1)).toISOString().split("T")[0] : ""}
                                    disabled={!startDate}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                                />
                                {LeaveFormData.formatted_end_date && (
                                    <p className="mt-2 text-sm text-green-600 font-medium">
                                        📅 {LeaveFormData.formatted_end_date}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Reason */}
                        <div>
                            <label htmlFor="reason" className="block text-sm font-semibold text-gray-700 mb-2">
                                Reason <span className="text-gray-400 font-normal">(optional)</span>
                            </label>
                            <textarea
                                id="reason"
                                name="reason"
                                value={LeaveFormData.reason}
                                onChange={handleChange}
                                placeholder="Provide a brief reason for your leave..."
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                rows="4"
                            ></textarea>
                        </div>

                        {/* Submit Button */}
                        <div className="flex justify-end gap-3 pt-4">
                            <button
                                type="button"
                                onClick={() => navigate(-1)}
                                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className={`px-6 py-3 rounded-lg flex items-center gap-2 font-medium transition-colors ${
                                    loading
                                        ? 'bg-blue-300 text-white cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                            >
                                <Save size={18} />
                                {loading ? 'Submitting...' : 'Submit Request'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {isPopupVisible && (
                <InformationalPopup
                    title={popupTitle}
                    message={popupMessage}
                />
            )}
        </div>
    );
}
